import { redeemDemoHandoff } from "../services/demoEntryAnalysisApi";
import { isDemoHandoffInvalid } from "./demoEntryAnalysisErrors";
import { readDemoSession, writeDemoSession } from "./demoEntryAnalysisSession";

export type RedeemHandoffResult = "ok" | "already_spent" | "no_code" | "failed";

/** Codes we already POSTed (or saw as invalid) — survives React remounts in the same tab. */
const spentHandoffCodes = new Set<string>();

/** In-flight redeem by code — Strict Mode double-mount shares one network call. */
const inFlightRedeem = new Map<string, Promise<RedeemHandoffResult>>();

/**
 * Redeem handoff_code at most once. Call only when poll/claim returned 403
 * (cookie missing) — never before the first credentials:include attempt.
 *
 * Drops handoff from sessionStorage after a successful or already-spent redeem
 * so remounts / signup return cannot spend it twice.
 */
export function redeemDemoHandoffOnce(jobId: string): Promise<RedeemHandoffResult> {
  const code = readDemoSession()?.handoffCode?.trim();
  if (!code) return Promise.resolve("no_code");

  if (spentHandoffCodes.has(code)) {
    writeDemoSession({ handoffCode: undefined });
    return Promise.resolve("already_spent");
  }

  const existing = inFlightRedeem.get(code);
  if (existing) return existing;

  const promise = (async (): Promise<RedeemHandoffResult> => {
    try {
      await redeemDemoHandoff(jobId, code);
      spentHandoffCodes.add(code);
      writeDemoSession({ handoffCode: undefined });
      return "ok";
    } catch (err) {
      if (isDemoHandoffInvalid(err)) {
        // Ticket already consumed (often Strict Mode / parallel redeem) — cookie may exist.
        spentHandoffCodes.add(code);
        writeDemoSession({ handoffCode: undefined });
        return "already_spent";
      }
      return "failed";
    } finally {
      inFlightRedeem.delete(code);
    }
  })();

  inFlightRedeem.set(code, promise);
  return promise;
}

/** True when a 403 can still attempt a one-shot redeem (code present or in-flight). */
export function canAttemptHandoffRedeem(): boolean {
  const code = readDemoSession()?.handoffCode?.trim();
  if (!code) return false;
  if (spentHandoffCodes.has(code)) return false;
  return true;
}
